import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Send, History, X } from 'lucide-react';
import clsx from 'clsx';
import SearchInput from './SearchInput';
import { DateInput, Textarea, Select } from '@components/common';
import { noteService, auditService } from '@services';
import { NoteEntityType, AuditHistoryEntry, ApiError } from '@app-types';
import { utcToLocal } from '@utils/dateUtils';
import Button from './Button';
import Tabs, { TabItem } from './Tabs';
import styles from './EntityActivity.module.css';

function toDateRangeError(dateFrom: string, dateTo: string): string {
  if (dateFrom && dateTo && dateFrom > dateTo) {
    return 'From date must be on or before To date';
  }
  return '';
}

interface EntityActivityProps {
  entityType: NoteEntityType;
  entityId: number;
  title?: string;
}

const EntityActivity: React.FC<EntityActivityProps> = ({ entityType, entityId, title = 'Activity' }) => {
  const [noteContent, setNoteContent] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'notes' | 'history'>('notes');

  // History filters
  const [selectedUser, setSelectedUser] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const queryClient = useQueryClient();

  // Fetch notes
  const { data: notesData, isLoading } = useQuery({
    queryKey: ['notes', entityType, entityId],
    queryFn: () => noteService.getNotes(entityType, entityId),
    enabled: !!entityId,
    staleTime: 30000 // Cache for 30 seconds
  });

  // Fetch audit history
  const { data: historyData, isLoading: isLoadingHistory } = useQuery({
    queryKey: ['audit-history', entityType, entityId],
    queryFn: () => auditService.getAuditHistory(entityType, entityId),
    enabled: !!entityId,
    staleTime: 30000 // Cache for 30 seconds
  });

  // Create note mutation
  const createNoteMutation = useMutation({
    mutationFn: (content: string) => noteService.createNote(entityType, entityId, { content }),
    onSuccess: () => {
      // Refetch notes after creating a new one; the backend also writes an audit
      // entry for the note, so the History tab must be invalidated as well
      queryClient.invalidateQueries({ queryKey: ['notes', entityType, entityId] });
      queryClient.invalidateQueries({ queryKey: ['audit-history', entityType, entityId] });
      setNoteContent('');
      setError('');
    },
    onError: (error: ApiError) => {
      // Handle different error response structures
      const errorMessage =
        error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Failed to create note';
      setError(String(errorMessage));
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!noteContent.trim()) {
      setError('Note content cannot be empty');
      return;
    }

    createNoteMutation.mutate(noteContent.trim());
  };

  const formatDate = (dateString: string) => {
    const date = utcToLocal(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatChangeValue = (value: string | null, type: string) => {
    if (value === null) return value;

    if (type === 'datetime') {
      try {
        const date = utcToLocal(value);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch {
        return value;
      }
    }

    if (type === 'date') {
      try {
        const date = utcToLocal(value);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch {
        return value;
      }
    }

    if (type === 'boolean') {
      const lowerValue = value.toLowerCase();
      if (lowerValue === 'true') return 'Yes';
      if (lowerValue === 'false') return 'No';
    }

    return value;
  };

  const tabs: TabItem[] = [
    { key: 'notes', label: 'Notes' },
    { key: 'history', label: 'History' }
  ];

  // Get unique users from history data
  const uniqueUsers = useMemo(() => {
    if (!historyData) return [];
    const users = new Set<string>();
    historyData.forEach((entry) => {
      const userName = entry.user_name || 'System';
      users.add(userName);
    });
    return Array.from(users).sort((a, b) => a.localeCompare(b));
  }, [historyData]);

  // Filter history data
  const dateRangeError = toDateRangeError(dateFrom, dateTo);
  const filteredHistory = useMemo(() => {
    if (!historyData) return [];

    return historyData.filter((entry: AuditHistoryEntry) => {
      // Filter by user
      if (selectedUser !== 'all') {
        const userName = entry.user_name || 'System';
        if (userName !== selectedUser) return false;
      }

      // Skip date filtering while the range is inverted so results stay visible
      if (!dateRangeError) {
        const entryDate = new Date(entry.created_at);
        if (dateFrom) {
          const fromDate = new Date(dateFrom);
          fromDate.setHours(0, 0, 0, 0);
          if (entryDate < fromDate) return false;
        }
        if (dateTo) {
          const toDate = new Date(dateTo);
          toDate.setHours(23, 59, 59, 999);
          if (entryDate > toDate) return false;
        }
      }

      // Filter by search term
      if (searchTerm.trim()) {
        const search = searchTerm.toLowerCase();
        const matchesNotes = entry.notes?.toLowerCase().includes(search);
        const matchesChanges = entry.changes?.some(
          (change) =>
            change.field_name.toLowerCase().includes(search) ||
            change.old_value?.toLowerCase().includes(search) ||
            change.new_value?.toLowerCase().includes(search)
        );
        if (!matchesNotes && !matchesChanges) return false;
      }

      return true;
    });
  }, [historyData, selectedUser, dateFrom, dateTo, searchTerm, dateRangeError]);

  const clearFilters = () => {
    setSelectedUser('all');
    setDateFrom('');
    setDateTo('');
    setSearchTerm('');
  };

  const hasActiveFilters = selectedUser !== 'all' || dateFrom || dateTo || searchTerm;

  // Render a single change in history
  const renderHistoryChange = (change: any) => {
    const isAddition = change.old_value === null;
    const isDeletion = change.new_value === null;
    const formattedOldValue = formatChangeValue(change.old_value, change.type);
    const formattedNewValue = formatChangeValue(change.new_value, change.type);

    // Determine which type of change to render
    let changeContent;
    if (isAddition) {
      changeContent = (
        <span className={clsx(styles.historyChangeValue, styles.historyChangeAdded)}>{formattedNewValue}</span>
      );
    } else if (isDeletion) {
      changeContent = (
        <span className={clsx(styles.historyChangeValue, styles.historyChangeRemoved)}>{formattedOldValue}</span>
      );
    } else {
      changeContent = (
        <>
          <span className={clsx(styles.historyChangeValue, styles.historyChangeOld)}>{formattedOldValue}</span>
          <span className={styles.historyChangeArrow}>→</span>
          <span className={clsx(styles.historyChangeValue, styles.historyChangeNew)}>{formattedNewValue}</span>
        </>
      );
    }

    return (
      <div key={`change-${change.field_name}-${change.old_value}-${change.new_value}`} className={styles.historyChange}>
        <span className={styles.historyChangeField}>{change.field_name}:</span>
        {changeContent}
      </div>
    );
  };

  // Render notes list content based on loading/data state
  const renderNotesList = () => {
    if (isLoading) {
      return <div className={styles.notesLoading}>Loading notes...</div>;
    }

    if (notesData && notesData.notes.length > 0) {
      return (
        <div className={styles.notesListItems}>
          {notesData.notes.map((note) => (
            <div key={note.id} className={styles.noteItem}>
              <div className={styles.noteItemMeta}>
                <div className={styles.noteItemAuthor}>{note.authorName}</div>
                <div className={styles.noteItemDate}>{formatDate(note.createdAt)}</div>
              </div>
              <div className={styles.noteItemContent}>{note.content}</div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className={styles.notesEmpty}>
        <MessageSquare size={48} className={styles.notesEmptyIcon} />
        <p>No notes yet</p>
        <p className={styles.notesEmptySubtitle}>Add the first note above</p>
      </div>
    );
  };

  // Render notes tab content
  const renderNotesTab = () => (
    <>
      {/* Create note form */}
      <form onSubmit={handleSubmit} className={styles.noteCreateForm}>
        <Textarea
          value={noteContent}
          onChange={(e) => {
            setNoteContent(e.target.value);
            if (error) setError('');
          }}
          placeholder="Add a note..."
          rows={3}
          disabled={createNoteMutation.isPending}
          error={error}
          dataTestId="activity-note"
        />
        <div className={styles.noteCreateActions}>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            loading={createNoteMutation.isPending}
            loadingText="Posting..."
            disabled={!noteContent.trim() || createNoteMutation.isPending}
          >
            <Send size={16} />
            <span>Post Note</span>
          </Button>
        </div>
      </form>

      {/* Notes list */}
      <div className={styles.notesList} data-test-id="notes-list">
        {renderNotesList()}
      </div>
    </>
  );

  // Render history empty state
  const renderHistoryEmpty = () => {
    if (historyData && historyData.length > 0) {
      return (
        <div className={styles.notesEmpty}>
          <History size={48} className={styles.notesEmptyIcon} />
          <p>No results found</p>
          <p className={styles.notesEmptySubtitle}>Try adjusting your filters</p>
        </div>
      );
    }

    return (
      <div className={styles.notesEmpty}>
        <History size={48} className={styles.notesEmptyIcon} />
        <p>No history yet</p>
        <p className={styles.notesEmptySubtitle}>Changes will appear here</p>
      </div>
    );
  };

  // Render history list content based on loading/data state
  const renderHistoryList = () => {
    if (isLoadingHistory) {
      return <div className={styles.notesLoading}>Loading history...</div>;
    }

    if (filteredHistory.length > 0) {
      return (
        <div className={styles.historyTimeline}>
          {filteredHistory.map((entry) => (
            <div key={entry.id} className={styles.historyEntry} data-test-id="history-entry">
              <div className={styles.historyEntryMeta}>
                <div className={styles.historyEntryAuthor}>{entry.user_name || 'System'}</div>
                <div className={styles.historyEntryDate}>{formatDate(entry.created_at)}</div>
              </div>
              <div className={styles.historyEntryContent}>
                {entry.changes && entry.changes.length > 0 && (
                  <div className={styles.historyChanges}>
                    {entry.changes.map((change) => renderHistoryChange(change))}
                  </div>
                )}
                {entry.notes && (
                  <div className={styles.historyEntryNotes}>
                    <div className={styles.historyEntryNotesLabel}>Notes:</div>
                    <div className={styles.historyEntryNotesContent}>{entry.notes}</div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }

    return renderHistoryEmpty();
  };

  // Render history tab content
  const renderHistoryTab = () => (
    <>
      {/* History filters */}
      <div className={styles.historyFilters}>
        <div className={styles.historyFiltersRow}>
          <div className={styles.historyFilterGroup}>
            <Select
              id="user-filter"
              label="User:"
              value={selectedUser}
              onChange={(value) => setSelectedUser(value)}
              options={[
                { value: 'all', label: 'All Users' },
                ...uniqueUsers.map((user) => ({ value: user, label: user }))
              ]}
              dataTestId="user-filter"
            />
          </div>

          <div className={styles.historyFilterGroup}>
            <DateInput
              id="date-from"
              label="From:"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              max={dateTo || undefined}
              dataTestId="date-from"
              wrapperClassName={styles.historyFilterDateWrapper}
            />
          </div>

          <div className={styles.historyFilterGroup}>
            <DateInput
              id="date-to"
              label="To:"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              min={dateFrom || undefined}
              error={dateRangeError || undefined}
              dataTestId="date-to"
              wrapperClassName={styles.historyFilterDateWrapper}
            />
          </div>

          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search by field name or values..."
            className={styles.historyFilterSearch}
          />

          <button
            onClick={clearFilters}
            className={styles.historyFilterClear}
            title="Clear all filters"
            disabled={!hasActiveFilters}
          >
            <X size={16} />
            Clear
          </button>
        </div>
      </div>

      {/* History list */}
      <div className={styles.historyList} data-test-id="history-list">
        {renderHistoryList()}
      </div>
    </>
  );

  return (
    <div className={styles.notesSection} data-test-id="notes-section">
      <div className={styles.notesHeader}>
        <div className={styles.notesHeaderTitle}>
          <MessageSquare size={20} />
          <h3>{title}</h3>
        </div>
        <div className={styles.notesHeaderTabs}>
          <Tabs items={tabs} activeTab={activeTab} onTabChange={(key) => setActiveTab(key as 'notes' | 'history')} />
        </div>
      </div>

      <div className={styles.notesContent}>{activeTab === 'notes' ? renderNotesTab() : renderHistoryTab()}</div>
    </div>
  );
};

export default EntityActivity;
